"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { AppShell } from "@/components/atsa/AppShell";
import { KeyboardCheck } from "@/components/atsa/KeyboardCheck";
import { ModeSelector } from "@/components/atsa/ModeSelector";
import { Results } from "@/components/atsa/Results";
import { TestChrome } from "@/components/atsa/TestChrome";
import { ATSA_PROFILE_2026, type PracticeMode } from "@/lib/atsa/atsaConfig";
import { generateAssignmentSet, type AssignmentPart, type AssignmentSet, type AssignmentVariable } from "@/lib/atsa/valueAssignments";

type Phase = "options" | "showing" | "answering" | "transition" | "result";
type ResultItem = { part: AssignmentPart; correct: boolean; variable: AssignmentVariable; reactionMs: number };

export default function ValueAssignmentsPage() {
  const [mode, setMode] = useState<PracticeMode>("training");
  const [phase, setPhase] = useState<Phase>("options");
  const [parts, setParts] = useState<AssignmentPart[]>([1, 2, 3]);
  const [setsPerPart, setSetsPerPart] = useState(5);
  const [displayMs, setDisplayMs] = useState(1750);
  const [partIndex, setPartIndex] = useState(0);
  const [setIndex, setSetIndex] = useState(0);
  const [displayIndex, setDisplayIndex] = useState(0);
  const [promptIndex, setPromptIndex] = useState(0);
  const [currentSet, setCurrentSet] = useState<AssignmentSet | null>(null);
  const [results, setResults] = useState<ResultItem[]>([]);
  const [feedback, setFeedback] = useState<"good" | "bad" | null>(null);
  const [seedInput, setSeedInput] = useState("20260613");
  const [currentSeed, setCurrentSeed] = useState(20260613);
  const timeoutRef = useRef<number | null>(null);
  const promptAtRef = useRef(0);
  const sessionSeedRef = useRef(20260613);

  const effectiveSets = mode === "simulation" ? ATSA_PROFILE_2026.values.setsPerPart : setsPerPart;
  const effectiveParts: AssignmentPart[] = mode === "simulation" ? [1, 2, 3] : parts;
  const effectiveDisplayMs = mode === "simulation" ? ATSA_PROFILE_2026.values.displayMs : displayMs;

  const clearTimer = useCallback(() => {
    if (timeoutRef.current !== null) window.clearTimeout(timeoutRef.current);
    timeoutRef.current = null;
  }, []);

  const showSet = useCallback((partPosition: number, setPosition: number) => {
    clearTimer();
    const part = effectiveParts[partPosition]!;
    const setSeed = sessionSeedRef.current + (partPosition + 1) * 1_000_003 + (setPosition + 1) * 7_919;
    const nextSet = generateAssignmentSet(part, setSeed);
    setCurrentSet(nextSet);
    setPartIndex(partPosition);
    setSetIndex(setPosition);
    setDisplayIndex(0);
    setPromptIndex(0);
    setPhase("showing");
  }, [clearTimer, effectiveParts]);

  const start = useCallback(() => {
    const parsedSeed = Number.parseInt(seedInput, 10);
    const seed = Number.isFinite(parsedSeed) ? parsedSeed >>> 0 : 20260613;
    sessionSeedRef.current = seed;
    setCurrentSeed(seed);
    setResults([]);
    setFeedback(null);
    showSet(0, 0);
  }, [seedInput, showSet]);

  useEffect(() => {
    if (phase !== "showing" || !currentSet) return;
    clearTimer();
    timeoutRef.current = window.setTimeout(() => {
      if (displayIndex < 2) {
        setDisplayIndex((value) => value + 1);
      } else {
        setPhase("answering");
        promptAtRef.current = performance.now();
      }
    }, effectiveDisplayMs + ATSA_PROFILE_2026.values.gapMs);
    return clearTimer;
  }, [clearTimer, currentSet, displayIndex, effectiveDisplayMs, phase]);

  const advanceAfterSet = useCallback(() => {
    if (setIndex + 1 < effectiveSets) {
      setPhase("transition");
      timeoutRef.current = window.setTimeout(() => showSet(partIndex, setIndex + 1), 500);
      return;
    }
    if (partIndex + 1 < effectiveParts.length) {
      setPhase("transition");
      timeoutRef.current = window.setTimeout(() => showSet(partIndex + 1, 0), 1200);
      return;
    }
    setPhase("result");
  }, [effectiveParts.length, effectiveSets, partIndex, setIndex, showSet]);

  useEffect(() => {
    if (phase !== "answering" || !currentSet) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.repeat) return;
      const simulationMatch = /^Numpad[1-4]$/.test(event.code);
      const trainingMatch = simulationMatch || /^[1-4]$/.test(event.key);
      if (mode === "simulation" ? !simulationMatch : !trainingMatch) return;
      event.preventDefault();
      const answer = Number(event.code.startsWith("Numpad") ? event.code.replace("Numpad", "") : event.key);
      const variable = currentSet.promptOrder[promptIndex]!;
      const correct = answer === currentSet.values[variable];
      setResults((items) => [...items, { part: currentSet.part, correct, variable, reactionMs: performance.now() - promptAtRef.current }]);
      if (mode === "training") {
        setFeedback(correct ? "good" : "bad");
        window.setTimeout(() => setFeedback(null), 220);
      }
      if (promptIndex < 2) {
        setPromptIndex((value) => value + 1);
        promptAtRef.current = performance.now();
      } else {
        advanceAfterSet();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [advanceAfterSet, currentSet, mode, phase, promptIndex]);

  useEffect(() => () => clearTimer(), [clearTimer]);

  const totalCorrect = results.filter((item) => item.correct).length;
  const accuracy = results.length ? Math.round((totalCorrect / results.length) * 100) : 0;
  const partScores = useMemo(() => ([1, 2, 3] as AssignmentPart[]).map((part) => {
    const items = results.filter((item) => item.part === part);
    return items.length ? Math.round((items.filter((item) => item.correct).length / items.length) * 100) : 0;
  }), [results]);
  const activeSimulation = mode === "simulation" && phase !== "options" && phase !== "result";
  const activePart = effectiveParts[partIndex] ?? 1;
  const promptVariable = currentSet?.promptOrder[promptIndex];

  return (
    <AppShell hideChrome={activeSimulation}>
      <TestChrome
        title="Value Assignments"
        mode={activeSimulation ? "simulation" : "training"}
        status={phase !== "options" && phase !== "result" ? `Part ${activePart} of 3 · Set ${setIndex + 1} of ${effectiveSets}` : undefined}
        onRestart={mode === "training" && phase !== "options" ? start : undefined}
      >
        {phase === "options" && (
          <section className="panel">
            <div className="panel-header">
              <p className="eyebrow">REPORTED SECTION 2</p>
              <h2>Value Assignments</h2>
              <p>Retain A, B, and C while direct assignments become linked arithmetic relationships. Final values stay between 1 and 4.</p>
            </div>
            <ModeSelector value={mode} onChange={(next) => {
              setMode(next);
              if (next === "simulation") {
                setParts([1, 2, 3]);
                setSetsPerPart(10);
                setDisplayMs(1750);
              }
            }} />
            {mode === "training" && (
              <div className="options-grid">
                <div className="profile-block">
                  <span className="profile-label">Difficulty profile</span>
                  <div className="profile-row">
                    {([['Light', 'Warm-up', 5, 2500, false], ['Standard', 'Test-like', 10, 1750, true], ['Hard', 'Harder', 20, 1250, false]] as const).map(([label, tag, sp, ms, std]) => (
                      <button type="button" key={label} className={`profile-chip ${setsPerPart === sp && displayMs === ms ? "active" : ""}`} onClick={() => { setSetsPerPart(sp); setDisplayMs(ms); }}>
                        <span className="tag">{tag}</span>{label}{std ? <span className="std-flag">★ Standard</span> : null}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="control-group">
                  <label htmlFor="value-part">Parts</label>
                  <select id="value-part" value={parts.join(",")} onChange={(event) => {
                    const value = event.target.value;
                    setParts(value === "1,2,3" ? [1, 2, 3] : [Number(value) as AssignmentPart]);
                  }}>
                    <option value="1,2,3">All three parts</option>
                    <option value="1">Part 1 only</option>
                    <option value="2">Part 2 only</option>
                    <option value="3">Part 3 only</option>
                  </select>
                </div>
                <div className="control-group">
                  <label htmlFor="sets-per-part">Sets per part</label>
                  <select id="sets-per-part" value={setsPerPart} onChange={(event) => setSetsPerPart(Number(event.target.value))}>
                    <option value={3}>3</option><option value={5}>5</option><option value={10}>10</option><option value={20}>20</option>
                  </select>
                </div>
                <div className="control-group">
                  <label htmlFor="assignment-speed">Display time</label>
                  <select id="assignment-speed" value={displayMs} onChange={(event) => setDisplayMs(Number(event.target.value))}>
                    <option value={2500}>2.5 seconds</option><option value={1750}>1.75 seconds</option><option value={1250}>1.25 seconds</option><option value={900}>0.9 seconds</option>
                  </select>
                </div>
              </div>
            )}
            <div className="control-group">
              <label htmlFor="values-seed">Replay seed</label>
              <div className="seed-control-row">
                <input id="values-seed" inputMode="numeric" value={seedInput} onChange={(event) => setSeedInput(event.target.value.replace(/\D/g, "").slice(0, 10))} />
                <button type="button" className="secondary-button" onClick={() => setSeedInput(String(Date.now() >>> 0))}>New seed</button>
              </div>
              <small className="field-note">Use the same seed to reproduce every assignment set and prompt order.</small>
            </div>
            <ol className="instructions">
              <li>Assignments always display in A, B, C order.</li>
              <li>Recall prompts use a randomized order.</li>
              <li>Part 1 uses direct values. Part 2 introduces one relationship. Part 3 uses a two-link dependency chain.</li>
              <li>Fidelity simulation contains three parts and ten sets per part.</li>
            </ol>
            <KeyboardCheck />
            <button className="primary-button" onClick={start}>Start {mode === "simulation" ? "Simulation" : "Training"}</button>
          </section>
        )}

        {phase === "showing" && currentSet && (
          <section className={activeSimulation ? "fidelity-stage assignment-stage" : "stage"}>
            <div className="assignment-card fidelity-card">
              {mode === "training" && <p className="prompt-label">MEMORIZE</p>}
              <div className="assignment-expression">{currentSet.displays[displayIndex]}</div>
            </div>
          </section>
        )}

        {phase === "answering" && currentSet && promptVariable && (
          <section className={activeSimulation ? "fidelity-stage assignment-stage" : "stage"}>
            <div className="assignment-card fidelity-card">
              {mode === "training" && <p className="prompt-label">ENTER THE VALUE</p>}
              <div className="assignment-expression">{promptVariable} = ?</div>
              {feedback && <div className={`feedback ${feedback}`}>{feedback === "good" ? "CORRECT" : "WRONG"}</div>}
            </div>
          </section>
        )}

        {phase === "transition" && (
          <section className={activeSimulation ? "fidelity-stage" : "stage"}><div className="stage-content"><p>Next set…</p></div></section>
        )}

        {phase === "result" && (
          <Results
            section="Value Assignments"
            record={{ sectionSlug: "value-assignments", score: accuracy, scoreLabel: "Accuracy" }}
            title="Value Assignments Complete"
            metrics={[
              { label: "Overall", value: `${accuracy}%` },
              { label: "Part 1", value: `${partScores[0]}%` },
              { label: "Part 2", value: `${partScores[1]}%` },
              { label: "Part 3", value: `${partScores[2]}%` },
              { label: "Correct", value: totalCorrect },
              { label: "Responses", value: results.length },
              { label: "Replay seed", value: currentSeed },
            ]}
            onAgain={start}
            onOptions={() => setPhase("options")}
          />
        )}
      </TestChrome>
    </AppShell>
  );
}
